import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import Send from 'assets/send_button';
import { setFullScreen } from '../../../../../../store/actions';
import { useIsTouchMobile } from '../../../../../../utils/use-is-mobile';
import './style.scss';

const Sender = ({
    sendMessage,
    inputTextFieldHint,
    disabledInput,
    userInput,
    embedded,
    mobileFullScreenOnFocus,
    fullScreenMode,
    dispatch,
}) => {
    const [inputValue, setInputValue] = useState('');
    const formRef = useRef('');
    const textareaRef = useRef(null);
    const isMobile = useIsTouchMobile();
    const disabled = disabledInput || userInput === 'disable';

    function handleChange(e) {
        setInputValue(e.target.value);
    }

    function handleSubmit(e) {
        sendMessage(e);
        setInputValue('');
        // Touch mobile: dismiss the keyboard so the reply is readable. Deliberate
        // departure from messenger norms (see spec 2026-10-15, §2). Deliberately
        // not gated on `embedded` - floating mode dismisses the keyboard too.
        if (isMobile && textareaRef.current) {
            textareaRef.current.blur();
        }
    }

    function handleFocus(e) {
        // Embedded on mobile: take over the viewport so the composer stays
        // above the keyboard (MobileTakeover). Floating mode is already full
        // viewport while open. The DOM `disabled` state (not the prop) is
        // checked so a programmatic focus on a disabled composer is a no-op.
        // Never inside a frame: an embedded page in a partner iframe cannot
        // escape the frame (spec §9).
        if (
            typeof window !== 'undefined' &&
            window.self === window.top &&
            embedded &&
            isMobile &&
            mobileFullScreenOnFocus &&
            !fullScreenMode &&
            !e.target.disabled
        ) {
            dispatch(setFullScreen(true));
        }
    }

    function onEnterPress(e) {
        if (e.keyCode === 13 && e.shiftKey === false) {
            e.preventDefault();
            // by dispatching the event we trigger onSubmit
            // formRef.current.submit() would not trigger onSubmit
            formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    }
    return userInput === 'hide' ? (
        <div />
    ) : (
        <form ref={formRef} className="rw-sender" onSubmit={handleSubmit}>
            <TextareaAutosize
                ref={textareaRef}
                type="text"
                minRows={1}
                onKeyDown={onEnterPress}
                onFocus={handleFocus}
                maxRows={3}
                onChange={handleChange}
                value={inputValue}
                className="rw-new-message"
                name="message"
                placeholder={inputTextFieldHint}
                disabled={disabled}
                // Opening the keyboard on page load is hostile on phones.
                autoFocus={!isMobile}
                autoComplete="off"
            />
            <button
                type="submit"
                className="rw-send"
                disabled={!(inputValue && inputValue.length > 0)}
            >
                <Send
                    className="rw-send-icon"
                    ready={!!(inputValue && inputValue.length > 0)}
                    alt="send"
                />
            </button>
        </form>
    );
};
const mapStateToProps = (state) => ({
    userInput: state.metadata.get('userInput'),
    fullScreenMode: state.behavior.get('fullScreenMode', false),
});

Sender.propTypes = {
    sendMessage: PropTypes.func,
    inputTextFieldHint: PropTypes.string,
    disabledInput: PropTypes.bool,
    userInput: PropTypes.string,
    embedded: PropTypes.bool,
    mobileFullScreenOnFocus: PropTypes.bool,
    fullScreenMode: PropTypes.bool,
    dispatch: PropTypes.func,
};

Sender.defaultProps = {
    embedded: false,
    mobileFullScreenOnFocus: true,
};

export default connect(mapStateToProps)(Sender);
